import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { CaseBriefFlagAffordance } from "@/zapi-frontend/features/FlagDeficiency";
import { caseBriefEditorExtensions } from "@/zapi-frontend/widgets/CreateProjectChat/lib/caseBriefEditorExtensions";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { CaseFrameEditorMarks } from "./caseFrameEditorMarks";

type TCaseFrameSectionEditorProps = {
  body: string;
  onChange: (body: string) => void;
  placeholder: string;
  showTemplatePrompts: boolean;
  readOnly: boolean;
  caseBriefId: number | null;
};

const normalized = (body: string) => body.replace(/\n+$/, "");

// One section of the case brief, edited in place; the heading stays in the frame's label column.
export function CaseFrameSectionEditor({
  body,
  onChange,
  placeholder,
  showTemplatePrompts,
  readOnly,
  caseBriefId
}: TCaseFrameSectionEditorProps) {
  const editor = useEditor({
    extensions: [...caseBriefEditorExtensions(placeholder), CaseFrameEditorMarks],
    content: normalized(body),
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => onChange(ed.storage.markdown.getMarkdown()),
    editorProps: { attributes: { class: "outline-hidden" } }
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.storage.templatePlaceholders.enabled = showTemplatePrompts;
    editor.storage.templatePlaceholders.className = "text-muted-foreground/70";
    editor.storage.templatePlaceholders.labelClassName = "text-muted-foreground/70";
    editor.view.dispatch(editor.state.tr);
  }, [editor, showTemplatePrompts]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (editor.storage.markdown.getMarkdown() === normalized(body)) return;
    editor.commands.setContent(normalized(body));
  }, [body, editor]);

  useEffect(() => {
    editor?.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  return (
    <>
      <EditorContent
        editor={editor}
        className={cn(
          // Lines and list items are spaced like the frame's claim rows.
          "typo-body-md text-foreground group-data-[state=pending]/section:[&_.ProseMirror]:pb-1",
          "[&_.ProseMirror_p]:my-1 [&_.ProseMirror_p]:py-1.5 [&_.ProseMirror>p:first-child]:mt-0.5 [&_.ProseMirror>p:last-child]:mb-0.5 [&_.ProseMirror_strong]:font-medium",
          "[&_.ProseMirror_h3]:typo-label-md [&_.ProseMirror_h3]:pt-3 [&_.ProseMirror_h3]:pb-0.5 [&_.ProseMirror_h3]:text-muted-foreground group-data-[state=pending]/section:[&_.ProseMirror_h3]:text-muted-foreground/70 [&_.ProseMirror>h3:first-child]:pt-2.5 [&_.ProseMirror_h3+p]:mt-0",
          "[&_.ProseMirror_h4]:typo-label-sm [&_.ProseMirror_h4]:pt-2 [&_.ProseMirror_h4]:text-muted-foreground [&_.ProseMirror_h4+p]:mt-0",
          "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ps-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ps-5 [&_.ProseMirror_li]:my-1 [&_.ProseMirror_li]:py-1.5 [&_.ProseMirror_li_p]:my-0 [&_.ProseMirror_li_p]:py-0",
          "[&_.ProseMirror>:is(ul,ol):first-child>li:first-child]:mt-0.5 [&_.ProseMirror>:is(ul,ol):last-child>li:last-child]:mb-0.5 [&_.ProseMirror_:is(h3,h4)+:is(ul,ol)>li:first-child]:mt-0",
          "[&_.ProseMirror_li::marker]:text-muted-foreground group-data-[state=pending]/section:[&_.ProseMirror_li::marker]:text-muted-foreground/70",
          !readOnly &&
            "[&_.ProseMirror>p]:-mx-1 [&_.ProseMirror>p]:px-1 [&_.ProseMirror>p]:rounded-md [&_.ProseMirror_li]:rounded-md [&_.ProseMirror_:is(p,li)]:transition-colors [&_.ProseMirror>p:hover]:bg-foreground/[0.04] [&_.ProseMirror_li:hover]:bg-foreground/[0.04] [&_.ProseMirror_.is-editing]:bg-foreground/[0.05]",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground/70 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
        )}
      />
      {caseBriefId ? <CaseBriefFlagAffordance editor={editor} caseBriefId={caseBriefId} /> : null}
    </>
  );
}
